/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { formCategories } from "@Helpers/constants";
import { formCategoryObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import { useEffect, useState } from "react";
import { useTranslation, type TranslatedString } from "@i18n";
import _ from "lodash";
import { getTemplates } from "@Actions/Forms";
import { objectIdString } from "@Helpers/schemas/common.schema";
interface FormTemplatePreviewPopupProps {
  /** `null` before a template is chosen — `defaultProps` below supplies that default. */
  formTemplateId?: string | null;
  width?: string;
  isVisible?: boolean;
  onCancel: () => void;
  /** A key of `formCategories` ("FILES", "ISSUES", …), used to look up the default section. */
  resourceType?: string;
  /** DEAD PROP: passed by `FormTemplate` but never read here. Declared so that call site checks. */
  projectId?: string;
}

/**
 * The fetched template plus the id it was fetched for, so the effect can skip a redundant request.
 * All three are `null` until the first fetch resolves, which is what drives the shimmer. `template`
 * is `unknown` because `FormPreview`'s own `formData` prop is `unknown` — it re-narrows internally.
 */
type PreviewTemplateInfo = {
  headerTitle: TranslatedString | null;
  template: unknown;
  /** `undefined` too: the prop it mirrors is optional. */
  currentSelectedFormTemplateId: string | null | undefined;
};

const FormTemplatePreviewPopup = ({
  formTemplateId,
  width,
  isVisible,
  onCancel,
  resourceType,
}: FormTemplatePreviewPopupProps) => {
  const defaultTemplateInfo: PreviewTemplateInfo = {
    headerTitle: null,
    template: null,
    currentSelectedFormTemplateId: null,
  };

  const { t } = useTranslation();
  const [formTemplateInfo, setFormTemplateInfo] = useState(defaultTemplateInfo);

  const renderContentShimmer = () => {
    const shimmerInfo = [
      { id: "sampleField_1", labelWidth: "w-20", height: "h-10" },
      { id: "sampleField_2", labelWidth: "w-16", height: "h-10" },
      { id: "sampleField_3", labelWidth: "w-24", height: "h-10" },
      { id: "sampleField_4", labelWidth: "w-20", height: "h-20" },
      { id: "sampleField_5", labelWidth: "w-16", height: "h-10" },
      { id: "sampleField_6", labelWidth: "w-24", height: "h-10" },
    ];
    return (
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <div className="h-6 bg-slate-200 rounded animate-pulse w-32" />
          <div className="h-4 w-4 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="space-y-4">
          {shimmerInfo.map((field) => (
            <div key={field.id} className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 bg-slate-200 rounded animate-pulse" />
                <div className={`h-4 bg-slate-200 rounded animate-pulse ${field.labelWidth}`} />
              </div>
              <div className={`bg-slate-200 rounded animate-pulse ${field.height}`} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderHeaderTitleShimmer = () => {
    return <div className="h-6 bg-slate-200 rounded animate-pulse w-48" />;
  };

  useEffect(() => {
    const handleSetTemplateSchemaAndShowPreview = async () => {
      if (formTemplateInfo.currentSelectedFormTemplateId !== formTemplateId) {
        if (isVisible) {
          // The prop is a plain nullable string while `getTemplatesRequest.formId` is a branded
          // `objectIdString`, so it is parsed rather than asserted. Only a row click opens this
          // popup, so an unusable id means there is nothing to preview: keep the shimmer instead of
          // firing a request `templateDefaultSchema.parse` would throw on.
          const formId = objectIdString.safeParse(formTemplateId).data;
          if (!formId) {
            setFormTemplateInfo(defaultTemplateInfo);
            return;
          }
          const result = await getTemplates({
            formId,
            isArchived: false,
          });
          const newFormTemplateSchema = _.get(result, "data.result.template", null);
          const newFormTemplateName = _.get(result, "data.result.name", null);
          setFormTemplateInfo({
            template: newFormTemplateSchema,
            headerTitle: t("custom_template_form_preview_header", {
              header: newFormTemplateName,
            }),
            currentSelectedFormTemplateId: formTemplateId,
          });
        } else {
          setFormTemplateInfo(defaultTemplateInfo);
        }
      }
    };
    handleSetTemplateSchemaAndShowPreview();
  }, [formTemplateId, isVisible]);

  return (
    <ModalStateDialogComponent
      isVisible={isVisible}
      width={width}
      headerTitle={formTemplateInfo.headerTitle ? formTemplateInfo.headerTitle : renderHeaderTitleShimmer()}
      onCancel={onCancel}
      modelContent={
        <div className="mb-2">
          {formTemplateInfo.template ? (
            <FormPreview
              formData={{ template: formTemplateInfo.template }}
              defaultSection={
                formCategoryObject[(formCategories as Record<string, string>)[resourceType!]!]?.defaultSection
              }
            />
          ) : (
            renderContentShimmer()
          )}
        </div>
      }
    />
  );
};

FormTemplatePreviewPopup.defaultProps = {
  width: "83%",
  formTemplateId: null,
};

export default FormTemplatePreviewPopup;
